import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import fetchClient from 'interfaces/fetchClient';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import env from 'variables/config';
import { isValidEmail, isValidSiren } from 'utils/verification';
import SearchSelector from 'components/fields/SearchSelector';
import InstallPWAButton from 'components/InstallPWAButton';
import PaymentScheduleEditor from 'components/fields/PaymentScheduleEditor';
import {
    decodeLegacyDepositPreset,
    DEFAULT_CUSTOM_SCHEDULE,
    CUSTOM_SCHEDULE_OPTION,
} from 'utils/paymentSchedule';

const BusinessInfo = () => {
    const [user, setUser] = useState(null);
    const ownerId = localStorage.getItem('ownerId');

    const initUser = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        fetchClient
            .get(`${env.apiUrl}/user/me`, config)
            .then((res) => {
                const user = res.data;
                if (!Array.isArray(user.acceptedPaymentMethods)) user.acceptedPaymentMethods = [];
                setUser(user);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleChange = (e: any) => {
        const { id, value } = e.target;
        setUser({ ...user, [id]: value });
    };

    const handleCheckboxChange = (e: any) => {
        const { id, checked } = e.target;
        setUser((prev: any) => {
            const current: string[] = Array.isArray(prev?.acceptedPaymentMethods)
                ? prev.acceptedPaymentMethods
                : [];
            const next = checked
                ? [...new Set([...current, id])]
                : current.filter((m) => m !== id);
            return { ...prev, acceptedPaymentMethods: next };
        });
    };

    const updateProfil = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        const data = {
            billingEmail: user?.billingEmail,
            billingAddress: user?.billingAddress,
            city: user?.city,
            postalCode: user?.postalCode,
            siren: user?.siren,
            activityCode: user?.activityCode,
            vatNumber: user?.vatNumber,
            address: user?.address,
            website: user?.website,
            paymentMethod: user?.paymentMethod,
            legalNotice: user?.legalNotice,
            cgv: user?.cgv,
            acceptedPaymentMethods: user?.acceptedPaymentMethods,
            invoiceDueDelay: user?.invoiceDueDelay,
            paymentSchedule: user?.paymentSchedule,
            rib: user?.rib,
            secondRib: user?.secondRib,
            ics: user?.ics,
            bic: user?.bic,
            phone: user?.phone,
        };
        fetchClient
            .put(`${env.apiUrl}/user`, data, config)
            .then((res) => {
                toast.success('Votre profil a été modifié avec succès');
            })
            .catch((err) => {
                toast.error(err?.response?.data?.error || 'Erreur lors de la mise à jour du profil');
                console.log(err);
            });
    };

    useEffect(() => {
        initUser();
    }, []);

    return (
        <>
            {!ownerId ? (
                <Card extra={'w-full mt-3 px-6 py-6 border'}>
                    {/* Header */}
                    <div className="w-full px-[8px]">
                        <h4 className="text-xl font-medium">
                            Votre entreprise
                        </h4>
                        <p className="mt-2 text-base text-gray-400">
                            Ces informations vont figurer sur les factures
                        </p>
                    </div>
                    {/* inputs */}
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <InputField
                            label="Adresse mail"
                            value={user?.billingEmail}
                            placeholder={user?.billingEmail || user?.email}
                            id="billingEmail"
                            type="text"
                            onChange={handleChange}
                            error="incorrect"
                            isInvalid={!isValidEmail(user?.billingEmail)}
                            inputSmall={true}
                        />
                        <InputField
                            label="SIREN"
                            value={user?.siren}
                            placeholder={user?.siren || '902894317'}
                            id="siren"
                            type="text"
                            onChange={handleChange}
                            error="incorrect"
                            isInvalid={!isValidSiren(user?.siren)}
                            inputSmall={true}
                        />
                        <InputField
                            label="Code d'activité"
                            value={user?.activityCode}
                            placeholder={user?.activityCode || '6201Z'}
                            id="activityCode"
                            type="text"
                            onChange={handleChange}
                            inputSmall={true}
                        />
                        <InputField
                            label="Numéro de TVA"
                            value={user?.vatNumber}
                            placeholder={user?.vatNumber || 'FR902894317'}
                            id="vatNumber"
                            type="text"
                            onChange={handleChange}
                            error="incorrect"
                            //isInvalid={!isValidVATNumber(user?.vatNumber)}
                            inputSmall={true}
                        />
                        <InputField
                            label="Adresse"
                            value={user?.billingAddress}
                            placeholder={
                                user?.billingAddress ||
                                '1 rue de la paix'
                            }
                            id="billingAddress"
                            type="text"
                            onChange={handleChange}
                            inputSmall={true}
                        />
                        <InputField
                            label="Ville"
                            value={user?.city}
                            placeholder={user?.city || 'Paris'}
                            id="city"
                            type="text"
                            onChange={handleChange}
                            inputSmall={true}
                        />
                        <InputField
                            label="Code postal"
                            value={user?.postalCode}
                            placeholder={user?.postalCode || '75001'}
                            id="postalCode"
                            type="text"
                            onChange={handleChange}
                            inputSmall={true}
                        />
                        <InputField
                            label="Site internet"
                            value={user?.website}
                            placeholder={user?.website || 'toola.eu'}
                            id="website"
                            type="text"
                            onChange={handleChange}
                            error="incorrect"
                            isInvalid={
                                user?.website && !user?.website.includes('.')
                            }
                            inputSmall={true}
                        />
                        
                        <div>
                            <label
                                htmlFor={'paymentMethodOptions'}
                                className={`text-xs font-medium text-navy-700`}
                            >
                                Moyens de paiement acceptés
                            </label>
                            <div className="mt-2 flex gap-3">
                                {[
                                    { display: 'Chèque', value: 'check' },
                                    { display: 'Espèce', value: 'cash' },
                                    { display: 'Virement', value: 'transfer' },
                                    {
                                        display: 'Carte bancaire',
                                        value: 'credit-card',
                                    },
                                    {
                                        display: 'Prélèvement',
                                        value: 'direct-debit',
                                    },
                                    {
                                        display: 'LCR',
                                        value: 'LCR',
                                    },
                                ].map((option, index) => (
                                    <div className="flex gap-2">
                                        <p className="text-xs mb-1">
                                            {option.display}
                                        </p>
                                        <input
                                            key={`checkbox-${index}`}
                                            id={option.value}
                                            type="checkbox"
                                            checked={user?.acceptedPaymentMethods.includes(
                                                option.value,
                                            )}
                                            onChange={handleCheckboxChange}
                                            className="w-4 h-4 text-toola-500 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                        ></input>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="col-span-2">
                        <InputField
                            label="Téléphone"
                            value={user?.phone}
                            placeholder={user?.phone || '0612345678'}
                            id="phone"
                            type="text"
                            onChange={handleChange}
                            inputSmall={true}
                        />
                        </div>
                        <div className="col-span-2">
                            <InputField
                                label="Informations de paiement"
                                value={user?.paymentMethod}
                                placeholder={
                                    user?.paymentMethod || 'IBAN: ..., BIC: ...'
                                }
                                id="paymentMethod"
                                type="text"
                                onChange={handleChange}
                                inputSmall={true}
                            />
                        </div>
                        <div className="mb-4 col-span-2 z-50">
                            <SearchSelector
                                label="Échéances des factures"
                                displayKey="name"
                                value={user?.invoiceDueDelay}
                                inputSmall={true}
                                options={[
                                    {
                                        _id: 'direct',
                                        name: 'À réception',
                                    },
                                    {
                                        _id: '7-days',
                                        name: '7 jours',
                                    },
                                    {
                                        _id: '10-days',
                                        name: '10 jours',
                                    },
                                    {
                                        _id: '15-days',
                                        name: '15 jours',
                                    },
                                    {
                                        _id: '20-days',
                                        name: '20 jours',
                                    },
                                    {
                                        _id: '30-days',
                                        name: '30 jours',
                                    },
                                    {
                                        _id: 'end-of-month',
                                        name: 'Fin du mois',
                                    },
                                    {
                                        _id: '30-days-end-of-month',
                                        name: '30 jours fin de mois',
                                    },
                                    {
                                        _id: '45-days',
                                        name: '45 jours',
                                    },
                                    {
                                        _id: '60-days',
                                        name: '60 jours',
                                    },
                                    {
                                        _id: '90-days',
                                        name: '90 jours',
                                    },
                                    {
                                        _id: '20pct-deposit-80pct-30days',
                                        name: '20% acompte + 80% à 30 jours',
                                    },
                                    {
                                        _id: '50pct-deposit-50pct-30days',
                                        name: '50% acompte + 50% à 30 jours',
                                    },
                                    CUSTOM_SCHEDULE_OPTION,
                                ]}
                                onSelect={(e: any) => {
                                    const decoded = decodeLegacyDepositPreset(e._id);
                                    setUser({
                                        ...user,
                                        invoiceDueDelay: e._id,
                                        paymentSchedule:
                                            e._id === CUSTOM_SCHEDULE_OPTION._id
                                                ? user?.paymentSchedule?.length
                                                    ? user.paymentSchedule
                                                    : decodeLegacyDepositPreset(user?.invoiceDueDelay) ||
                                                      DEFAULT_CUSTOM_SCHEDULE
                                                : decoded || [],
                                    });
                                }}
                            />
                            {user?.invoiceDueDelay === CUSTOM_SCHEDULE_OPTION._id && (
                                <div className="mt-4 rounded-xl border border-gray-100 p-3 dark:border-white/10">
                                    <PaymentScheduleEditor
                                        value={user?.paymentSchedule || []}
                                        onChange={(rows) =>
                                            setUser({ ...user, paymentSchedule: rows })
                                        }
                                    />
                                </div>
                            )}
                        </div>
                        <div className="col-span-2">
                            <InputField
                                label="Mentions légales"
                                value={user?.legalNotice}
                                placeholder={user?.legalNotice || ' '}
                                id="legalNotice"
                                type="text"
                                onChange={handleChange}
                                inputSmall={true}
                            />
                        </div>
                        <div className="col-span-2">
                            <label
                                htmlFor="cgv"
                                className="text-xs font-medium text-navy-700"
                            >
                                Conditions Générales de Vente (CGV)
                            </label>
                            <p className="text-xs text-gray-400 mb-1">
                                Si renseignées, une page CGV sera automatiquement ajoutée à la fin de chaque facture.
                            </p>
                            <textarea
                                id="cgv"
                                value={user?.cgv || ''}
                                placeholder="Saisir vos conditions générales de vente..."
                                onChange={handleChange}
                                rows={8}
                                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-navy-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-toola-500 resize-y"
                            />
                        </div>
                        <div className="col-span-2">
                            <InputField
                                label="RIB"
                                value={user?.rib}
                                placeholder={user?.rib || ' '}
                                id="rib"
                                type="text"
                                onChange={handleChange}
                                inputSmall={true}
                            />
                        </div>
                        <div className="col-span-2">
                            <InputField
                                label="RIB particulier"
                                value={user?.secondRib}
                                placeholder={user?.secondRib || ' '}
                                id="secondRib"
                                type="text"
                                onChange={handleChange}
                                inputSmall={true}
                            />
                        </div>
                        <InputField
                            label="BIC"
                            value={user?.bic}
                            placeholder={user?.bic || 'BANKFRPP'}
                            id="bic"
                            type="text"
                            onChange={handleChange}
                            inputSmall={true}
                        />
                        <InputField
                            label="Identifiant Créancier SEPA (ICS)"
                            value={user?.ics}
                            placeholder={user?.ics || 'FR00ZZZ123456'}
                            id="ics"
                            type="text"
                            onChange={handleChange}
                            inputSmall={true}
                        />
                        <p className="col-span-2 -mt-2 text-xs text-gray-400">
                            Requis pour générer un fichier de Prélèvement SEPA. Cet identifiant vous est communiqué par votre banque (cf. brochure CFONB "Le prélèvement SEPA").
                        </p>
                    </div>
                    <div className="mt-3 flex w-full items-center justify-between">
                        <InstallPWAButton />
                        <button
                            className="linear rounded-xl bg-toola-500 px-8 py-2 text-sm font-medium text-white transition duration-200 hover:cursor-pointer hover:bg-toola-600 active:bg-toola-700 dark:bg-toola-400 dark:text-white dark:hover:bg-toola-300 dark:active:bg-toola-200"
                            onClick={() => updateProfil()}
                        >
                            Sauvegarder
                        </button>
                    </div>
                </Card>
            ) : (
                <Card extra={'w-full mt-3 px-6 py-6 border'}>
                    <p className="text-sm font-medium">
                        Vous devez contactez le propriétaire de l'entreprise
                        pour modifier les informations d'entreprise et de
                        facturation.
                    </p>
                </Card>
            )}
        </>
    );
};

export default BusinessInfo;
